import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/api/client";
import type { MilestoneTemplateRow } from "@/api/types";

const KEY = ["milestone-templates"] as const;

export function useMilestoneTemplates() {
  return useQuery<MilestoneTemplateRow[]>({
    queryKey: KEY,
    queryFn: () => apiRequest("/api/milestone-templates"),
  });
}

interface CreatePayload {
  study_kind: string;
  slug: string;
  label: string;
  display_order: number;
  notes?: string | null;
}

export function useCreateMilestoneTemplate() {
  const qc = useQueryClient();
  return useMutation<MilestoneTemplateRow, Error, CreatePayload>({
    mutationFn: (payload) =>
      apiRequest("/api/milestone-templates", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

interface UpdatePayload {
  id: string;
  label?: string;
  display_order?: number;
  notes?: string | null;
}

export function useUpdateMilestoneTemplate() {
  const qc = useQueryClient();
  return useMutation<MilestoneTemplateRow, Error, UpdatePayload>({
    mutationFn: ({ id, ...patch }) =>
      apiRequest(`/api/milestone-templates/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteMilestoneTemplate() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiRequest(`/api/milestone-templates/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
